import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  autobuyApi,
  equipmentApi,
  eventsApi,
  inventoryApi,
  mapApi,
  miscApi,
  playerApi,
  shopApi,
  skillsApi,
  waifuApi,
} from './endpoints';
import { queryKeys } from './queryKeys';
import type {
  AutobuySettingsUpdate,
  BuyItemRequest,
  CreateCharacterRequest,
  EquipMimicRequest,
  EquipPantsuRequest,
  LearnSkillRequest,
  PlayerSettings,
  UseItemRequest,
} from '@/types';

// ============ Queries ============

export function usePlayer(userId: number | null | undefined, enabled = true) {
  return useQuery({
    queryKey: userId ? queryKeys.player(userId) : ['player', 'none'],
    queryFn: () => playerApi.get(userId as number),
    enabled: !!userId && enabled,
    staleTime: 5_000,
  });
}

export function useWaifus(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.waifus(userId) : ['waifus', 'none'],
    queryFn: () => waifuApi.list(userId as number),
    enabled: !!userId,
    staleTime: 10_000,
  });
}

export function useWaifu(waifuId: number | null | undefined) {
  return useQuery({
    queryKey: waifuId ? queryKeys.waifu(waifuId) : ['waifu', 'none'],
    queryFn: () => waifuApi.get(waifuId as number),
    enabled: !!waifuId,
  });
}

export function useWaifuReference() {
  return useQuery({
    queryKey: queryKeys.reference(),
    queryFn: () => waifuApi.reference(),
    staleTime: 5 * 60_000,
  });
}

export function useRelationships(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.relationships(userId) : ['relationships', 'none'],
    queryFn: () => miscApi.relationships(userId as number),
    enabled: !!userId,
  });
}

export function useExplored(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.explored(userId) : ['explored', 'none'],
    queryFn: () => miscApi.explored(userId as number),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useMapLocations() {
  return useQuery({
    queryKey: queryKeys.mapLocations(),
    queryFn: () => mapApi.locations(),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useMapPositions(enabled = true) {
  return useQuery({
    queryKey: queryKeys.mapPositions(),
    queryFn: () => mapApi.positions(),
    enabled,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

export function useInventory(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.inventory(userId) : ['inventory', 'none'],
    queryFn: () => inventoryApi.get(userId as number),
    enabled: !!userId,
  });
}

export function useWaifusForItem(userId: number | null | undefined, itemId: string | null) {
  return useQuery({
    queryKey:
      userId && itemId
        ? queryKeys.waifusForItem(userId, itemId)
        : ['inventory', 'none'],
    queryFn: () => inventoryApi.waifusForItem(userId as number, itemId as string),
    enabled: !!userId && !!itemId,
  });
}

export function useItemCount(userId: number | null | undefined, itemId: string | null) {
  return useQuery({
    queryKey:
      userId && itemId ? queryKeys.itemCount(userId, itemId) : ['inventory', 'none'],
    queryFn: () => inventoryApi.itemCount(userId as number, itemId as string),
    enabled: !!userId && !!itemId,
  });
}

export function useShop(userId: number | null | undefined, enabled = true) {
  return useQuery({
    queryKey: userId ? queryKeys.shop(userId) : ['shop', 'none'],
    queryFn: () => shopApi.get(userId as number),
    enabled: !!userId && enabled,
  });
}

export function useShopCheck(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.shopCheck(userId) : ['shop', 'none'],
    queryFn: () => shopApi.check(userId as number),
    enabled: !!userId,
    refetchInterval: 30_000,
  });
}

export function useConsumables() {
  return useQuery({
    queryKey: queryKeys.shopConsumables(),
    queryFn: () => shopApi.consumables(),
    staleTime: 10 * 60_000,
  });
}

export function useAutobuy(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.autobuy(userId) : ['autobuy', 'none'],
    queryFn: () => autobuyApi.get(userId as number),
    enabled: !!userId,
  });
}

export function useSkillsCatalog() {
  return useQuery({
    queryKey: queryKeys.skills(),
    queryFn: () => skillsApi.getAll(),
    staleTime: 10 * 60_000,
  });
}

export function usePlayerSkills(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.playerSkills(userId) : ['player', 'none', 'skills'],
    queryFn: () => skillsApi.getPlayer(userId as number),
    enabled: !!userId,
  });
}

export function useEvents(userId: number | null | undefined, limit = 10) {
  return useQuery({
    queryKey: userId ? queryKeys.events(userId) : ['events', 'none'],
    queryFn: () => eventsApi.recent(userId as number, limit),
    enabled: !!userId,
    refetchInterval: 15_000,
  });
}

export function useEventsArchive(
  userId: number | null | undefined,
  page: number,
  perPage = 20,
) {
  return useQuery({
    queryKey:
      userId ? queryKeys.eventsArchive(userId, page) : ['events', 'none', 'archive', page],
    queryFn: () => eventsApi.archive(userId as number, page, perPage),
    enabled: !!userId,
  });
}

export function useEquipment(waifuId: number | null | undefined, userId: number | undefined) {
  return useQuery({
    queryKey: waifuId
      ? queryKeys.waifuEquipment(waifuId, userId)
      : ['waifu', 'none', 'equipment'],
    queryFn: () => equipmentApi.get(waifuId as number, userId),
    enabled: !!waifuId,
  });
}

export function usePlayerPantsu(userId: number | null | undefined) {
  return useQuery({
    queryKey: userId ? queryKeys.playerPantsu(userId) : ['player', 'none', 'pantsu'],
    queryFn: () => equipmentApi.playerPantsu(userId as number),
    enabled: !!userId,
  });
}

export function useAllPantsu() {
  return useQuery({
    queryKey: queryKeys.allPantsu(),
    queryFn: () => equipmentApi.allPantsu(),
    staleTime: 10 * 60_000,
  });
}

export function useLevelTable() {
  const q = useQuery({
    queryKey: queryKeys.levelTable(),
    queryFn: () => miscApi.levelTable(),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
  const getNextLevelXp = (level: number): number | undefined => {
    const entry = q.data?.levels.find((l) => l.level === level);
    return entry?.next_level_xp;
  };
  return { ...q, getNextLevelXp };
}

export function useWeaponTiers() {
  return useQuery({
    queryKey: queryKeys.weaponTiers(),
    queryFn: () => miscApi.weaponTiers(),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useArmorTiers() {
  return useQuery({
    queryKey: queryKeys.armorTiers(),
    queryFn: () => miscApi.armorTiers(),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useItems() {
  return useQuery({
    queryKey: queryKeys.items(),
    queryFn: () => miscApi.items(),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}

// ============ Mutations ============

function invalidatePlayer(qc: ReturnType<typeof useQueryClient>, userId: number) {
  qc.invalidateQueries({ queryKey: queryKeys.player(userId) });
}

function invalidateWaifus(qc: ReturnType<typeof useQueryClient>, userId: number) {
  qc.invalidateQueries({ queryKey: queryKeys.waifus(userId) });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCharacterRequest) => playerApi.create(body),
    onSuccess: (_data, vars) => {
      invalidatePlayer(qc, vars.user_id);
      invalidateWaifus(qc, vars.user_id);
    },
  });
}

export function useUpdatePlayerSettings(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlayerSettings) => playerApi.updateSettings(userId, body),
    onSuccess: () => invalidatePlayer(qc, userId),
  });
}

export function useSetTravelTarget(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => playerApi.setTravelTarget(userId, locationId),
    onSuccess: () => {
      invalidatePlayer(qc, userId);
      qc.invalidateQueries({ queryKey: queryKeys.mapPositions() });
    },
  });
}

export function useClearTravelTarget(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => playerApi.clearTravelTarget(userId),
    onSuccess: () => {
      invalidatePlayer(qc, userId);
      qc.invalidateQueries({ queryKey: queryKeys.mapPositions() });
    },
  });
}

export function useLearnSkill(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LearnSkillRequest) => skillsApi.learn(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.playerSkills(userId) });
      invalidatePlayer(qc, userId);
    },
  });
}

export function useAllocateStat(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (statName: string) => waifuApi.allocateStat(waifuId, statName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifu(waifuId) });
      invalidateWaifus(qc, userId);
    },
  });
}

export function useAllocateTrait(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (traitId: string) => waifuApi.allocateTrait(waifuId, traitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifu(waifuId) });
      invalidateWaifus(qc, userId);
    },
  });
}

export function useUseItem(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UseItemRequest) => inventoryApi.use(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
      invalidatePlayer(qc, userId);
      invalidateWaifus(qc, userId);
    },
  });
}

export function useBuyItem(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BuyItemRequest) => shopApi.buy(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.shop(userId) });
      qc.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
      invalidatePlayer(qc, userId);
    },
  });
}

export function useUpdateAutobuy(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutobuySettingsUpdate) => autobuyApi.update(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.autobuy(userId) });
    },
  });
}

export function useTriggerAutouse(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => autobuyApi.trigger(userId),
    onSuccess: (data) => {
      if (data.used && data.used.length > 0) {
        invalidatePlayer(qc, userId);
        invalidateWaifus(qc, userId);
        qc.invalidateQueries({ queryKey: queryKeys.inventory(userId) });
      }
    },
  });
}

export function useUpgradeWeapon(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => equipmentApi.upgradeWeapon(waifuId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      invalidateWaifus(qc, userId);
      invalidatePlayer(qc, userId);
    },
  });
}

export function useUpgradeArmor(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => equipmentApi.upgradeArmor(waifuId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      invalidateWaifus(qc, userId);
      invalidatePlayer(qc, userId);
    },
  });
}

export function useEquipPantsu(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EquipPantsuRequest) => equipmentApi.equipPantsu(waifuId, userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.playerPantsu(userId) });
      invalidateWaifus(qc, userId);
    },
  });
}

export function useEquipMimic(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EquipMimicRequest) => equipmentApi.equipMimic(waifuId, userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.playerPantsu(userId) });
      invalidateWaifus(qc, userId);
    },
  });
}

export function useUnequipPantsu(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => equipmentApi.unequipPantsu(waifuId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.playerPantsu(userId) });
      invalidateWaifus(qc, userId);
    },
  });
}

export function useEquipRing(waifuId: number, userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => equipmentApi.equipRing(waifuId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waifuEquipment(waifuId, userId) });
      invalidateWaifus(qc, userId);
    },
  });
}
